<template>
    <div class="edit-flyout">
        <form class="inner hd-container">
            <header class="header">
                <h2 class="subhead-2">Create My Itinerary</h2>
            </header>
            <div class="fields">
                <fieldset class="form-field">
                    <label for="itineraryName">
                        <h3 class="card-header">Itinerary Name</h3>
                        <p class="required">Required</p>
                    </label>
                    <!-- Errors are tied to the field so they are read with it. -->
                    <input
                        v-model="itineraryName"
                        type="text"
                        id="itineraryName"
                        :aria-invalid="characterLimitWarning || duplicateItinerary ? 'true' : undefined"
                        :aria-describedby="[characterLimitWarning && lengthErrorId, duplicateItinerary && duplicateErrorId].filter(Boolean).join(' ') || undefined" />
                    <p v-show="characterLimitWarning" :id="lengthErrorId" class="required red">Name must be between 3 and 40 characters.</p>
                    <p v-show="duplicateItinerary" :id="duplicateErrorId" class="required red">You already have an itinerary with this name.</p>
                    <!-- Said in text, not only in a title tooltip on the
                         disabled button, which touch and screen reader users
                         never see. -->
                    <p v-if="!isOnline" :id="offlineNoteId" class="required">Unavailable offline. Reconnect to create an itinerary.</p>
                </fieldset>
            </div>

            <div class="apply-button-wrapper">
                <button type="submit" @click.prevent="handleClick" class="create-button small-heavy uppercase" :disabled="!applyActive || !isOnline" :title="!isOnline ? 'Unavailable offline' : undefined" :aria-describedby="!isOnline ? offlineNoteId : undefined" :class="{ active: applyActive && isOnline }">Create Itinerary</button>

                <button type="button" @click.prevent="close" class="cancel-button small-heavy uppercase">Cancel</button>
            </div>
        </form>
    </div>
</template>

<script setup>
    // Offline read-only — the store refuses these writes; the control must
    // not look live. See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
    const { isOnline } = useOnlineStatus();
    const lengthErrorId = useId();
    const duplicateErrorId = useId();
    const offlineNoteId = useId();
    // Stores
    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    // import { routeLocationKey } from '../../.nuxt/vue-router';
    const { getOhcList, updateOhcList } = useUtils();

    const { itinerary } = defineProps({
        itinerary: {
            type: [Object, null],
            required: false,
        },
    });

    const applyActive = ref(false);
    const route = useRoute();

    const itineraryName = ref('');
    const ohcItineraries = ref([]);
    const duplicateItinerary = ref(false);
    const characterLimitWarning = ref(false);

    const emit = defineEmits(['closeFlyout', 'closeFlyoutWithName']);
    function closeWithName() {
        emit('closeFlyoutWithName', itineraryName);
    }
    function close() {
        emit('closeFlyout');
    }
    defineExpose({
        close,
    });

    watch(itineraryName, (newVal, oldVal) => {
        duplicateItinerary.value = false;
        characterLimitWarning.value = false;

        if (itineraryName.value.length) {
            applyActive.value = true;
        } else {
            applyActive.value = false;
        }
    });

    onMounted(() => {
        if (import.meta.client) {
            const { ohcList } = getOhcList('ohcItineraries');
            ohcItineraries.value = ohcList;
        }
    });

    function addItinerary(clonedList) {
        if (itinerary) {
            clonedList.push({
                name: itineraryName.value,
                sites: itinerary.sites,
            });
        } else {
            clonedList.push({
                name: itineraryName.value,
                sites: [],
            });
        }

        updateOhcList('ohcItineraries', clonedList);
        userStore.resetItineraries(clonedList);
        closeWithName();
    }

    function handleClick() {
        let { ohcList, clone } = getOhcList('ohcItineraries');
        ohcItineraries.value = ohcList;

        if (itineraryName.value && itineraryName.value.length >= 3 && itineraryName.value.length <= 40) {
            if (ohcItineraries.value && ohcItineraries.value.length) {
                const found = ohcItineraries.value.find((singleItinerary) => singleItinerary.name === itineraryName.value);
                if (found) {
                    duplicateItinerary.value = true;
                    return;
                } else {
                    addItinerary(clone);
                }
            } else {
                addItinerary(clone);
            }
        } else {
            characterLimitWarning.value = true;
        }
    }
</script>

<style lang="scss" scoped>
    .edit-flyout {
        // Gutters match the sites filter panel, which shares this same flyout:
        // 16px on mobile, 64px from tablet up (Filters/sites.vue). The form also
        // carries `.hd-container`, whose gutter DISAPPEARS above $hd + 32px on the
        // assumption that the window supplies the spacing — true for a page, but
        // inside a modal the container's edge IS the panel edge, so the label and
        // input sat flush against it at 1408px (Asana O2-43). Setting the padding
        // here rather than patching that breakpoint means the modal no longer
        // depends on the shared class for its spacing at any width.
        .inner {
            padding: 16px 16px 0;

            @media (min-width: $tablet) {
                padding: 64px 64px 0;
            }
        }

        
        .header {
            position: relative;
            text-align: center;
            margin-bottom: 34px;
            // Top spacing comes from .inner above, matching the filter panel. Kept
            // here it would stack on top of that and push the title too far down.
        
            .reset {
                position: absolute;
                right: 0;
                top: 2px;
            }
        
            .back-caret {
                position: absolute;
                left: 0;
                top: -3px;
            }
        }

        .form-field {
            label {
                display: flex;
                align-items: flex-end;
                margin-bottom: 10px;

                @media (min-width: $tablet) {
                    .required {
                        font-size: 14px;
                        line-height: 21px;
                    }
                }

                h3 {
                    margin-right: 10px;
                }
            }

            input {
                margin-bottom: 5px;
            }
        }

        .apply-button-wrapper {
            padding: 20px 0;
            background: $white;
            width: 100%;

            @media (min-width: $tablet) {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .create-button,
            .cancel-button {
                width: 100%;
                padding: 14px 12px 10px;
                font-family: $avenir;
                text-align: center;

                @media (min-width: $tablet) {
                    width: 40%;
                    margin: 0 10px;
                    border: 1px solid;
                    border-radius: 3.333em;
                    padding: 1em 2em;
                    transition: 0.25s all;
                }
            }
            .create-button {
                margin-bottom: 20px;
                background-color: $black;
                color: $white;

                @media (min-width: $tablet) {
                    margin-bottom: 0px;
                    border: 1px solid;
                    border-radius: 3.333em;
                    padding: 1em 2em;
                    transition: 0.25s all;

                    &:hover {
                        background-color: $gray-light;
                        color: $black;
                    }
                }
            }

            .cancel-button {
                background-color: $gray-light;
                border: 1px solid $gray-common;
                color: $black;

                &:hover {
                    background-color: $black;
                    color: $white;
                }
            }
        }
    }
</style>
